// Availability of a service item (packing, gift wrap, …) at the store's
// location + channel. Read from its `service_item_details` (or the entry
// itself when it carries the fields inline):
//  - inactive item, unavailable location or inactive channel → unavailable
//  - online-tracked with no stock left at the location        → out of stock
// Flags the payload omits don't block the service item.

function toBoolean(value) {
  if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
  return Boolean(value);
}

function isOff(value) {
  return value !== undefined && value !== null && !toBoolean(value);
}

// Nested service_item_details, or the entry itself when it carries the
// service item's own fields (locations / is_active) inline.
export function getServiceItemDetails(entry) {
  const nested = entry?.service_item_details ?? entry?.serviceItemDetails ?? entry?.details;
  if (nested && typeof nested === 'object') return nested;
  const hasId = entry?.service_item_id !== undefined || entry?.serviceItemId !== undefined;
  const hasOwnFields = Array.isArray(entry?.locations) || entry?.is_active !== undefined;
  return hasId && hasOwnFields ? entry : null;
}

function pickLocation(details, locationId) {
  const locations = Array.isArray(details?.locations) ? details.locations : [];
  if (locations.length === 0) return null;
  return locations.find((location) => location?.location_id === locationId) ?? locations[0];
}

function isChannelActive(location, channelId) {
  const channels = Array.isArray(location?.channels) ? location.channels : [];
  if (channels.length === 0) return true;
  const isActive = (channel) => !isOff(channel?.is_active);
  if (!channelId) return channels.some(isActive);
  const channel = channels.find((item) => item?.channel_id === channelId);
  return Boolean(channel) && isActive(channel);
}

/**
 * @param {object} entry service item entry or its details
 * @returns {{ isAvailable: boolean, isOutOfStock: boolean, trackInventory: boolean, availableQuantity?: number }}
 */
export function getServiceItemAvailability(entry, { locationId, channelId } = {}) {
  const details = getServiceItemDetails(entry);
  if (!details) return { isAvailable: true, isOutOfStock: false, trackInventory: false };

  const location = pickLocation(details, locationId);
  const trackInventory = toBoolean(
    location?.online_track_inventory ?? details?.online_track_inventory,
  );
  const quantity = Number(location?.available_quantity);
  const availableQuantity =
    location?.available_quantity !== undefined &&
    location?.available_quantity !== null &&
    Number.isFinite(quantity)
      ? quantity
      : undefined;

  return {
    isAvailable:
      !isOff(details.is_active) &&
      !isOff(location?.is_available) &&
      isChannelActive(location, channelId),
    isOutOfStock: trackInventory && availableQuantity !== undefined && availableQuantity <= 0,
    trackInventory,
    availableQuantity,
  };
}
